// src/hooks/useFileSearchAndFilters.ts
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FilterFilesParams } from './useCustomFileMap';
import { LocationFilterStore, buildFilterStoreKey } from './useLocationFilterStore';
import { EMPTY_FILTER_SELECTION, FilterSelection, isSelectionActive } from '../views/FileManager/components/FilterPanel';
import { FileManagerRoutes } from '../enums/fileManagerRoutes';
import { FileNode } from '../types/FileManagerTypes';
import Notification from '../shared/components/Notification';
import { NotificationTypes } from '../enums/notificationTypes';

const SEARCH_DEBOUNCE_MS = 500;
const FILTER_ERROR = 'Unable to apply filters. Please try again.';
const SEARCH_ERROR = 'Unable to perform search. Please try again.';

// TODO: send "" instead of null once the backend contract is confirmed.
const toDateParam = (date: string): string | null => date || null;

interface SyncOptions {
  /** Fetch even when the location has no filter and none was active. */
  force?: boolean;
  /** The current file map holds search results, not a browsable tree. */
  fromSearchResults?: boolean;
}

export interface UseFileSearchAndFiltersParams {
  filterStore: LocationFilterStore;
  currentFolderId: string;
  rootFolderId: string;
  setCurrentFolderId: (folderId: string) => void;
  filterFiles: (params: FilterFilesParams) => Promise<void>;
  updateMapping: () => Promise<void>;
}

/**
 * Owns the file browser's search box, filter panel and every listing fetch they
 * trigger, so the two features share one request-ordering rule instead of
 * racing each other:
 *
 * - filters are remembered per section + folder and re-applied on navigation;
 * - search is global and always starts unfiltered -- filters applied during a
 *   search live only for that search;
 * - every fetch takes a sequence ticket, and a superseded one may no longer
 *   touch the folder or the applied selection (`useCustomFileMap` drops its
 *   file map write in the same situation).
 */
export const useFileSearchAndFilters = ({
  filterStore,
  currentFolderId,
  rootFolderId,
  setCurrentFolderId,
  filterFiles,
  updateMapping,
}: UseFileSearchAndFiltersParams) => {
  const location = useLocation();
  const { getFilter, setFilter, clearFilter } = filterStore;

  const [searchInput, setSearchInput] = useState<string>('');
  const [searchLoading, setSearchLoading] = useState(false);
  const isSearchActive = searchInput.trim().length > 0;

  const [draftSelection, setDraftSelection] = useState<FilterSelection>(EMPTY_FILTER_SELECTION);
  const [appliedSelection, setAppliedSelection] = useState<FilterSelection>(EMPTY_FILTER_SELECTION);
  const [isFilterApplyLoading, setIsFilterApplyLoading] = useState(false);
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const isFilterActive = isSelectionActive(appliedSelection);

  const appliedSelectionRef = useRef(appliedSelection);
  useEffect(() => {
    appliedSelectionRef.current = appliedSelection;
  }, [appliedSelection]);

  // Ticket of the newest listing request; anything older is superseded.
  const latestSyncIdRef = useRef(0);
  // Suppresses the folder-change effect when a handler navigates and fetches itself.
  const prevFolderIdRef = useRef(currentFolderId);
  const wasSearchActiveRef = useRef(false);
  const preSearchFolderIdRef = useRef<string | null>(null);
  // Filters applied to the active search. Search is global, so these are never
  // written to the per-location store and are dropped when the search clears --
  // every new search therefore starts unfiltered.
  const searchSelectionRef = useRef<FilterSelection>(EMPTY_FILTER_SELECTION);

  const isRecents = location.pathname === FileManagerRoutes.RECENTS;

  const applyFilterSelection = useCallback(
    (selection: FilterSelection, searchText: string, inRecent: boolean, currentLocation?: string) =>
      filterFiles({
        searchInput: searchText,
        creator: [],
        fileType: selection.fileTypes,
        map: selection.maps,
        fromDate: toDateParam(selection.dateRange[0]),
        toDate: toDateParam(selection.dateRange[1]),
        inRecent,
        currentLocation,
      }),
    [filterFiles],
  );

  // Hands control to the search: clears the search-only filter set and the UI
  // mirroring it, and invalidates any in-flight location sync so it cannot
  // re-apply that location's filter on top of the search.
  const resetSelectionForSearch = useCallback(() => {
    latestSyncIdRef.current += 1;
    searchSelectionRef.current = EMPTY_FILTER_SELECTION;
    setAppliedSelection(EMPTY_FILTER_SELECTION);
    setDraftSelection(EMPTY_FILTER_SELECTION);
  }, []);

  // Single integration point for navigation: re-applies the stored filter of the
  // location being entered, or restores the full listing when leaving a filtered
  // one. `force` fetches even when neither applies; `fromSearchResults` marks
  // the current file map as search output rather than a real tree.
  const syncFilterForLocation = useCallback(
    async (pathname: string, folderId: string, { force = false, fromSearchResults = false }: SyncOptions = {}) => {
      const stored = getFilter(buildFilterStoreKey(pathname, folderId));
      const wasFilterActive = isSelectionActive(appliedSelectionRef.current);
      if (!stored && !wasFilterActive && !force) return;

      const syncId = ++latestSyncIdRef.current;
      setSearchLoading(true);
      try {
        if (stored) {
          // A location-scoped response carries no ancestors to merge with, and
          // search output has no real tree to take them from -- rebuild first.
          if (fromSearchResults) await updateMapping();
          await applyFilterSelection(stored, '', pathname === FileManagerRoutes.RECENTS, folderId);
        } else {
          await updateMapping();
        }
        if (syncId !== latestSyncIdRef.current) return;
        setAppliedSelection(stored ?? EMPTY_FILTER_SELECTION);
      } catch {
        Notification({ message: FILTER_ERROR, type: NotificationTypes.ERROR });
      } finally {
        setSearchLoading(false);
      }
    },
    [getFilter, applyFilterSelection, updateMapping],
  );

  // Folder navigation (open, breadcrumb, search-result jump) funnels through
  // currentFolderId, so one effect covers every entry point.
  useEffect(() => {
    if (prevFolderIdRef.current === currentFolderId) return;
    prevFolderIdRef.current = currentFolderId;
    if (isSearchActive) return; // search owns the fetch while active
    syncFilterForLocation(location.pathname, currentFolderId);
  }, [currentFolderId]);

  // ---------------------------------------------------------------------------
  // Debounced search. Search is global (results render as a tree from root); the
  // folder the user was in is remembered and restored when the search clears.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!isSearchActive) {
      // Refetch only when clearing a search -- mount is already covered by
      // useCustomFileMap's own mount fetch.
      if (wasSearchActiveRef.current) {
        const restoreFolderId = preSearchFolderIdRef.current ?? rootFolderId;
        preSearchFolderIdRef.current = null;
        searchSelectionRef.current = EMPTY_FILTER_SELECTION;
        prevFolderIdRef.current = restoreFolderId;
        setCurrentFolderId(restoreFolderId);
        // Restores the location's own filter (or the full listing).
        syncFilterForLocation(location.pathname, restoreFolderId, { force: true, fromSearchResults: true });
      }
      wasSearchActiveRef.current = false;
      return;
    }
    // Entering search: the current location's filter is ignored, not sent.
    if (!wasSearchActiveRef.current) {
      preSearchFolderIdRef.current = currentFolderId;
      resetSelectionForSearch();
    }
    wasSearchActiveRef.current = true;

    const timer = setTimeout(async () => {
      const syncId = ++latestSyncIdRef.current;
      setSearchLoading(true);
      try {
        // Search-only filters (empty until the user applies some mid-search).
        await applyFilterSelection(searchSelectionRef.current, searchInput, isRecents);
        // Superseded (search cleared mid-flight) -- leave the folder alone.
        if (syncId !== latestSyncIdRef.current) return;
        setCurrentFolderId(rootFolderId);
      } catch {
        Notification({ message: SEARCH_ERROR, type: NotificationTypes.ERROR });
      } finally {
        setSearchLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchInput]);

  // Section switch: ends any active search, then shows the section's own root --
  // re-applying the filter remembered for it, or the full listing when it has none.
  const prevPathnameRef = useRef(location.pathname);
  useEffect(() => {
    if (prevPathnameRef.current === location.pathname) return;
    prevPathnameRef.current = location.pathname;

    // VFSBrowser's own route effect resets the folder to root.
    prevFolderIdRef.current = rootFolderId;

    // Clear the search box without letting the clear-search branch restore the
    // folder the search started from -- that folder belongs to the old section.
    const wasSearching = wasSearchActiveRef.current;
    wasSearchActiveRef.current = false;
    preSearchFolderIdRef.current = null;
    resetSelectionForSearch();
    setSearchInput('');

    // This hook owns the section's listing fetch (useCustomFileMap skips its
    // route effect for the browser), so the switch always refetches.
    syncFilterForLocation(location.pathname, rootFolderId, { force: true, fromSearchResults: wasSearching });
  }, [location.pathname]);

  // Search result selection: clears the search and navigates to the item's
  // location. Folders are opened; files land in their parent folder.
  const onSelectSearchResult = useCallback(
    async (file: FileNode) => {
      const destinationId = file.isDir ? file.id : (file.parentId ?? rootFolderId);
      // Navigating explicitly -- suppress the clear-search restore branch.
      wasSearchActiveRef.current = false;
      preSearchFolderIdRef.current = null;
      searchSelectionRef.current = EMPTY_FILTER_SELECTION;
      prevFolderIdRef.current = destinationId;
      setSearchInput('');
      setCurrentFolderId(destinationId);
      await syncFilterForLocation(location.pathname, destinationId, { force: true, fromSearchResults: true });
    },
    [syncFilterForLocation, setCurrentFolderId, location.pathname],
  );

  const handleApplyFilters = useCallback(async () => {
    const [from, to] = draftSelection.dateRange;
    if (from && to && from > to) {
      Notification({ message: 'From Date cannot be later than To Date.', type: NotificationTypes.ERROR });
      return;
    }
    if (isFilterApplyLoading) return;
    setIsFilterApplyLoading(true);
    try {
      if (isSearchActive) {
        // Filters refine the global search only -- no currentLocation, and they
        // stay out of the per-location store (dropped when the search clears).
        await applyFilterSelection(draftSelection, searchInput, isRecents);
        searchSelectionRef.current = draftSelection;
      } else {
        if (isSelectionActive(draftSelection)) {
          await applyFilterSelection(draftSelection, '', isRecents, currentFolderId);
        } else {
          // Emptied draft applied = clear for this location.
          await updateMapping();
        }
        setFilter(buildFilterStoreKey(location.pathname, currentFolderId), draftSelection);
      }
      setAppliedSelection(draftSelection);
      setFilterDropdownOpen(false);
    } catch {
      Notification({ message: FILTER_ERROR, type: NotificationTypes.ERROR });
    } finally {
      setIsFilterApplyLoading(false);
    }
  }, [
    draftSelection,
    isFilterApplyLoading,
    searchInput,
    isSearchActive,
    isRecents,
    currentFolderId,
    applyFilterSelection,
    updateMapping,
    setFilter,
    location.pathname,
  ]);

  // Clear All goes through the same apply mechanism as Apply (loading state,
  // duplicate-request guard, error messaging) -- the user story doesn't say
  // otherwise, and it keeps both actions consistent. Clears only the current
  // location's stored filter; other locations keep theirs.
  const handleClearAllFilters = useCallback(async () => {
    if (isFilterApplyLoading) return;
    setIsFilterApplyLoading(true);
    try {
      setDraftSelection(EMPTY_FILTER_SELECTION);
      setAppliedSelection(EMPTY_FILTER_SELECTION);
      if (isSearchActive) {
        // Only the search-only set is cleared; the location the search started
        // from keeps its filter for when the search is cleared.
        searchSelectionRef.current = EMPTY_FILTER_SELECTION;
        await applyFilterSelection(EMPTY_FILTER_SELECTION, searchInput, isRecents);
      } else {
        clearFilter(buildFilterStoreKey(location.pathname, currentFolderId));
        await updateMapping();
      }
    } catch {
      Notification({ message: FILTER_ERROR, type: NotificationTypes.ERROR });
    } finally {
      setIsFilterApplyLoading(false);
    }
  }, [
    isFilterApplyLoading,
    isSearchActive,
    searchInput,
    isRecents,
    currentFolderId,
    clearFilter,
    applyFilterSelection,
    location.pathname,
    updateMapping,
  ]);

  const handleDraftSelectionChange = useCallback((partial: Partial<FilterSelection>) => setDraftSelection((prev) => ({ ...prev, ...partial })), []);

  const handleFilterDropdownOpenChange = useCallback(
    (open: boolean) => {
      setFilterDropdownOpen(open);
      if (open) setDraftSelection(appliedSelection);
    },
    [appliedSelection],
  );

  return {
    searchInput,
    setSearchInput,
    isSearchActive,
    searchLoading,
    draftSelection,
    isFilterActive,
    isFilterApplyLoading,
    filterDropdownOpen,
    onSelectSearchResult,
    handleApplyFilters,
    handleClearAllFilters,
    handleDraftSelectionChange,
    handleFilterDropdownOpenChange,
  };
};
